import { useState } from 'react'
import parsePhoneNumberFromString from 'libphonenumber-js'
import { Moment } from 'moment'
import * as R from 'ramda'
import isEmail from 'validator/lib/isEmail'
import isPostalCode, { PostalCodeLocale } from 'validator/lib/isPostalCode'
import isURL from 'validator/lib/isURL'

import DateFormat from '../constants/dateFormat'
import { REQUIRED_PASSWORD_LENGTH } from '../constants/defaults'
import i18n from '../locales/i18n'
import moment from '../momentLocale'
import {
  CustomFieldValidator,
  Field,
  FieldObject,
  FieldProp,
  FieldsState,
  ValidateOptions,
  ValidationType,
} from '../types'
import {
  handleFormCheckboxInput,
  handleFormNumberInput,
  handleFormSelectInput,
  handleFormTextInput,
  isDigit,
  resolveValueOrGetter,
} from './index'
import { capitalize } from './languageUtils'

const isEmailsListValid = (emails: string) =>
  emails.split(',').every(email => isEmail(email.trim()))

export function isEmailValid(email: string, nullable?: boolean) {
  return (nullable && email === '') || isEmail(email)
}

export function isPhoneValid(mobilePhone: string, nullable?: boolean) {
  const phoneNumber = parsePhoneNumberFromString(mobilePhone, 'US')
  return (nullable && mobilePhone === '') || Boolean(phoneNumber?.isValid())
}

export const isPhoneListValid = (phones: string) =>
  phones.split(',').every(phone => isPhoneValid(phone.trim()))

export function isPostalCodeValid(zipCode: string, country?: PostalCodeLocale) {
  return country
    ? isPostalCode(zipCode, country)
    : isPostalCode(zipCode, 'US') || isPostalCode(zipCode, 'CA')
}

export function isWebsiteValid(website: string) {
  return isURL(website)
}

export function isPasswordValid(password: string) {
  return password.length >= REQUIRED_PASSWORD_LENGTH
}

export function isPasswordConfirmationValid(
  confirmation: string,
  password: string,
) {
  return isPasswordValid(password) && password === confirmation
}

export function isLengthValid(
  minLength: number | null,
  maxLength: number | null,
) {
  return (value: string) =>
    (!minLength || value.length >= minLength) &&
    (!maxLength || value.length <= maxLength)
}

export function isDeaValid(dea: string) {
  return dea.length >= 1
}

export function isValidDate(
  date: string | number,
  format = moment.localeData().longDateFormat('L'),
) {
  return moment(date, format, true).isValid()
}

export function isValidTime(
  time: string,
  format = moment
    .localeData()
    // @ts-ignore
    .longDateFormat(DateFormat.FULL_TIME_WITH_MERIDIAN) || 'hh:mma',
) {
  return moment(time, format, true).isValid()
}

const defaultFormat = 'YYYY-MM-DDTHH:mm:ss.SSSSZ'
const alternativeFormat = 'YYYY-MM-DDTHH:mm:ssZ'
// This is a global format - we don't need to change it
export function isValidTimestamp(
  timestamp: string | Moment,
  formatOne = defaultFormat,
  formatTwo = alternativeFormat,
) {
  return (
    moment(timestamp, formatOne, true).isValid() ||
    moment(timestamp, formatTwo, true).isValid()
  )
}

type OptionsProps = {
  gapOptional?: boolean
  specialSymbolsOptional?: boolean
  specialSymbolsRequired?: boolean
}

export function hasAtLeastSingleLatinChar(value: string) {
  return /[a-zA-Z]/.test(value)
}

export function isAlfaNumericString(value?: string, options?: OptionsProps) {
  if (R.isNil(value)) {
    return false
  }

  if (options?.specialSymbolsRequired) {
    if (options?.gapOptional) {
      return /^(?=.*?\d)(?=.*?[a-zA-Z])(?=.*?[!@#;$%^&*)(+=._-])[a-zA-Z\d\s!@#;$%^&*)(+=._-]+$/g.test(
        value,
      )
    }
    return /^(?=.*?\d)(?=.*?[a-zA-Z])(?=.*?[!@#;$%^&*)(+=._-])[a-zA-Z\d!@#;$%^&*)(+=._-]+$/g.test(
      value,
    )
  }
  if (options?.specialSymbolsOptional) {
    if (options?.gapOptional) {
      return /^(?=.*?\d)(?=.*?[a-zA-Z])[a-zA-Z\d\s!@#;$%^&*)(+=._-]+$/g.test(
        value,
      )
    }
    return /^(?=.*?\d)(?=.*?[a-zA-Z])[a-zA-Z\d!@#;$%^&*)(+=._-]+$/g.test(value)
  }

  return /^(?=.*?\d)(?=.*?[a-zA-Z])[a-zA-Z\d]+$/.test(value)
}

export function isValidDuration(duration: string) {
  return /^[0-9]{2}:[0-5][0-9]$/.test(duration)
}

export function notEmptyFormField(value?: any) {
  return !R.isNil(value) && Boolean(value.toString().length)
}

export const getDigits = (str?: string) =>
  (str || '').split('').filter(isDigit).join('')

export const splitByCaseChange = R.replace(/([a-z\d])([A-Z])/g, '$1 $2')
export const removeDigits = R.replace(/\d/g, '')
export const replaceIdSuffix = R.replace(/(.+)_(id)_(.+)/, '$1')
export const removeUnderscore = R.replace(/_/g, '')
export const replaceExtraEmptySpaces = R.replace(/  +/g, ' ')

export const getHumanReadableFieldName = R.pipe(
  splitByCaseChange,
  R.toLower,
  capitalize,
  removeDigits,
  replaceIdSuffix,
  removeUnderscore,
  replaceExtraEmptySpaces,
  R.trim,
)

type ValidationMapType = {
  [x in ValidationType]: (value: any, ...args: any[]) => boolean
}

const validationMap: ValidationMapType = {
  required: notEmptyFormField,
  phoneRequired: value =>
    value && R.is(String, value) && Boolean(getDigits(value)),
  phone: value =>
    !value ||
    (R.is(String, value) && (!getDigits(value) || isPhoneValid(value))),
  phoneList: value =>
    !value ||
    (R.is(String, value) && (!getDigits(value) || isPhoneListValid(value))),
  email: value => !value || (R.is(String, value) && isEmailValid(value)),
  emailsList: value =>
    !value || (R.is(String, value) && isEmailsListValid(value)),
  zip: (value, country) =>
    !value ||
    (R.is(String, value) &&
      (!getDigits(value) || isPostalCodeValid(value, country))),
  date: value => !value || (R.is(String, value) && isValidDate(value)),
  time: value => !value || (R.is(String, value) && isValidTime(value)),
  timestamp: value => !value || isValidTimestamp(value),
  website: value => !value || (R.is(String, value) && isWebsiteValid(value)),
  duration: value =>
    !value || (R.is(String, value) && isValidDuration(value)),
  greaterThanZero: value => Number(value) > 0,
  nonNegative: value => Number(value) >= 0,
}

type ValidationMessagesMapType = {
  [x in ValidationType]?: string | ((fieldName: string) => string)
}

const validationMessagesMap: ValidationMessagesMapType = {
  required: (fieldName: string) =>
    i18n.t('Utils:VALIDATION.FIELD_IS_REQUIRED', {
      fieldName: getHumanReadableFieldName(fieldName),
    }),
  emailsList: i18n.t('Utils:VALIDATION.EMAILS_LIST'),
  phoneList: i18n.t('Utils:VALIDATION.PHONE_LIST'),
  phone: i18n.t('Utils:VALIDATION.PHONE'),
  phoneRequired: i18n.t('Utils:VALIDATION.PHONE_IS_REQUIRED'),
  greaterThanZero: i18n.t('Utils:VALIDATION.GREATER_THAN_ZERO'),
  nonNegative: i18n.t('Utils:VALIDATION.NON_NEGATIVE'),
  zip: i18n.t('Utils:VALIDATION.ERROR_MESSAGE_BY_TYPE', {
    errorType: i18n.t('Common:FORMS.ZIP_CODE').toLowerCase(),
  }),
}

export const getErrorMessageByType = (
  errorType: ValidationType,
  fieldName: string,
) => {
  const keyFromMap = validationMessagesMap[errorType]
  const message = resolveValueOrGetter(fieldName, keyFromMap)
  return (
    message || i18n.t('Utils:VALIDATION.ERROR_MESSAGE_BY_TYPE', { errorType })
  )
}

export const validateWithDefaultValidator = (
  value: any,
  validatorName: ValidationType,
  country?: string,
) => {
  const validator = validationMap[validatorName]
  const valid = validator ? validator(value, country) : true
  return !valid ? validatorName : undefined
}

export const validateWithCustomValidator = (
  state: FieldsState,
  name: string,
  customValidator: CustomFieldValidator,
) => {
  const valid = customValidator?.validator({ state, name, value: state[name] })
  return !valid ? customValidator?.validatorName : undefined
}

export const validateField = (field: FieldProp, state: FieldsState) =>
  (field.validators || [])
    .map(validator =>
      typeof validator === 'object'
        ? validateWithCustomValidator(state, field.name, validator)
        : validateWithDefaultValidator(
          state[field.name],
          validator,
          state.country?.value,
        ),
    )
    .filter(R.identity)

export const getFieldValid = (field: FieldProp, state: FieldsState) =>
  validateField(field, state).length === 0

const InputHandlersMap = {
  text: handleFormTextInput,
  number: handleFormNumberInput,
  toggle: handleFormCheckboxInput,
  select: handleFormSelectInput,
  none: R.identity,
}

export function getInitialState(fields: FieldProp[]): FieldsState {
  return fields.reduce((acc, { name, initialValue = '' }) => {
    acc[name] = initialValue
    return acc
  }, {} as FieldsState)
}

function useFields(fields: FieldProp[], groupRequiredErrors = true) {
  const [state, setState] = useState<FieldsState>(getInitialState(fields))
  const [touched, setTouched] = useState<boolean>(false)
  const [validateTag, setValidateTag] = useState<number>()

  const getFormData = (validate: boolean) =>
    R.pipe(
      R.map((field: FieldProp) => {
        const {
          name,
          label,
          type = 'text',
          initialValue,
          messages = {},
        } = field
        const setValue = (value: any) =>
          setState(prevState => ({ ...prevState, [name]: value }))

        const value = state[name]
        const inputHandler = InputHandlersMap[type]
        const errors = validate ? validateField(field, state) : []
        const valid = errors.length === 0
        const firstError = R.head(errors) as keyof typeof validationMessagesMap

        return {
          [name]: {
            name,
            label,
            value,
            initialValue,
            set: inputHandler(setValue) as Field['set'],
            setValue,
            errors,
            valid,
            message: !valid
              ? (messages[firstError] ??
                getErrorMessageByType(firstError, label || name))
              : undefined,
            open: false,
            validateTag,
          },
        }
      }),
      R.mergeAll,
    )(fields)

  const formData: FieldObject = getFormData(touched)
  const openField = fields.find(({ name }) => formData[name].errors.length > 0)
  const hasRequiredErrors = Object.values(formData).some(({ errors }) =>
    errors.includes('required'),
  )
  const open =
    !touched || (groupRequiredErrors && hasRequiredErrors) || !openField
      ? {}
      : {
        [openField.name]: { open: true },
      }

  const formDataWithValidations: FieldObject = R.mergeDeepRight(formData, open)

  const validate = (options?: ValidateOptions) => {
    setTouched(!options?.silent)
    setValidateTag(Math.random())
    return Object.values(fields).every(field => getFieldValid(field, state))
  }

  const reset = (newFields?: FieldProp[]) => {
    setState(getInitialState(newFields || fields))
    setTouched(false)
  }

  const updateState = (newState: FieldsState) =>
    setState({ ...state, ...newState })

  return {
    fields: formDataWithValidations,
    hasRequiredErrors,
    validate,
    reset,
    updateState,
    validateTag,
    touched,
  }
}

export default useFields
